import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AdminSettingsService } from '@lmc-app/core/services/admin-settings.service';
import { ApplicationPermissionKey } from '@lmc-app/core/constants/application-permissions.constant';
import { CoApplicantRemoveRestore, StaleDataNotification } from '@lmc-app/shared/interface/user.interface';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { UserOption, UserPickerComponent } from '@lmc-app/shared/components/user-picker/user-picker.component';

/** One card per permission; add an entry here (and its key on the backend) to add a card */
interface Permission {
  key: ApplicationPermissionKey;
  label: string;
  icon: string;
  hint: string;
  disabledHint: string;
}

const PERMISSIONS: Permission[] = [
  {
    key: 'canViewTimeline',
    label: 'View Timeline',
    icon: 'timeline',
    hint: 'See the Timeline tab on an application for the selected users',
    disabledHint: 'The Timeline tab is available for everyone while this is off',
  },
  {
    key: 'canEditSalesAgent',
    label: 'Edit Sales Agent',
    icon: 'badge',
    hint: 'Change the Sales Agent in Details',
    disabledHint: 'Everyone is allowed to change the Sales Agent while this is off',
  },
];

interface StaleDataOption {
  key: keyof StaleDataNotification;
  label: string;
  hint: string;
}

const STALE_DATA_OPTIONS: StaleDataOption[] = [
  {
    key: 'showStaleOnSave',
    label: 'Check before saving Details',
    hint: 'Before a save, offer to reload if the application changed',
  },
  {
    key: 'showStaleOnTabs',
    label: 'Check when switching tabs',
    hint: 'On each tab change, warn if the application changed',
  },
];

interface CoApplicantOption {
  key: keyof CoApplicantRemoveRestore;
  label: string;
  hint: string;
}

const COAPPLICANT_OPTIONS: CoApplicantOption[] = [
  {
    key: 'showHistory',
    label: 'Show History',
    hint: 'Show the History button with past removals and restores',
  },
  {
    key: 'enableRemove',
    label: 'Allow Remove',
    hint: 'Show the Remove Co-Applicant button',
  },
  {
    key: 'enableRestore',
    label: 'Allow Restore',
    hint: 'Show the Restore Co-Applicant button',
  },
];

/** Every co-applicant action is on until the setting is saved */
const DEFAULT_COAPPLICANT_REMOVE_RESTORE: CoApplicantRemoveRestore = {
  showHistory: true,
  enableRemove: true,
  enableRestore: true,
};

type SaveId = ApplicationPermissionKey | 'staleData' | 'coApplicant';
type SaveResponse = { success: boolean; message: string };

interface SaveQueue {
  inFlight: boolean;
  /** A change was made while saving; send the latest state once the current save ends */
  pending: boolean;
}

@Component({
  selector: 'app-application-permission-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UserPickerComponent,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSnackBarModule,
  ],
  templateUrl: './application-permission-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./application-permission-settings.component.scss'],
})
export class ApplicationPermissionSettingsComponent implements OnInit {
  private readonly _AdminSettingsService = inject(AdminSettingsService);
  private readonly _SnackBar = inject(MatSnackBar);

  readonly permissions = PERMISSIONS;
  readonly staleDataOptions = STALE_DATA_OPTIONS;
  readonly coApplicantOptions = COAPPLICANT_OPTIONS;

  users: UserOption[] = [];

  /** User IDs granted each permission, including IDs of users no longer active */
  selected: Record<ApplicationPermissionKey, number[]> = {
    canViewTimeline: [],
    canEditSalesAgent: [],
  };
  enabled: Record<ApplicationPermissionKey, boolean> = {
    canViewTimeline: false,
    canEditSalesAgent: false,
  };
  staleDataNotification: StaleDataNotification = {
    showStaleOnSave: false,
    showStaleOnTabs: false,
  };
  coApplicantRemoveRestore: CoApplicantRemoveRestore = { ...DEFAULT_COAPPLICANT_REMOVE_RESTORE };
  isLoading = false;

  /** Last state the server confirmed, restored when a save fails */
  private saved: {
    permissions: Record<ApplicationPermissionKey, { ids: number[]; enable: boolean }>;
    staleDataNotification: StaleDataNotification;
    coApplicantRemoveRestore: CoApplicantRemoveRestore;
  } = {
    permissions: {
      canViewTimeline: { ids: [], enable: false },
      canEditSalesAgent: { ids: [], enable: false },
    },
    staleDataNotification: { showStaleOnSave: false, showStaleOnTabs: false },
    coApplicantRemoveRestore: { ...DEFAULT_COAPPLICANT_REMOVE_RESTORE },
  };
  private saveQueues: Record<SaveId, SaveQueue> = {
    canViewTimeline: { inFlight: false, pending: false },
    canEditSalesAgent: { inFlight: false, pending: false },
    staleData: { inFlight: false, pending: false },
    coApplicant: { inFlight: false, pending: false },
  };

  /** Loads the current settings when the page opens */
  ngOnInit(): void {
    this.loadConfig();
  }

  /** Fetches permissions, stale data and co-applicant settings and employees, and records them as the last saved state */
  loadConfig(): void {
    this.isLoading = true;
    this._AdminSettingsService.getApplicationPermissionsConfig().subscribe({
      next: (res) => {
        const { permissions, staleDataNotification, employees } = res.data;
        const coApplicantRemoveRestore = { ...DEFAULT_COAPPLICANT_REMOVE_RESTORE, ...res.data.coApplicantRemoveRestore };
        this.users = employees;
        this.saved = {
          permissions: {
            canViewTimeline: {
              ids: [...permissions.canViewTimeline.allowedEmployeeIds],
              enable: permissions.canViewTimeline.enable,
            },
            canEditSalesAgent: {
              ids: [...permissions.canEditSalesAgent.allowedEmployeeIds],
              enable: permissions.canEditSalesAgent.enable,
            },
          },
          staleDataNotification: { ...staleDataNotification },
          coApplicantRemoveRestore: { ...coApplicantRemoveRestore },
        };
        PERMISSIONS.forEach(({ key }) => this.restorePermission(key));
        this.staleDataNotification = { ...staleDataNotification };
        this.coApplicantRemoveRestore = { ...coApplicantRemoveRestore };
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.showMessage(error.error?.message || 'Failed to load application permissions');
      },
    });
  }

  /** Stores the users picked for a permission and saves them right away */
  onUsersChange(key: ApplicationPermissionKey, ids: number[]): void {
    this.selected[key] = ids;
    this.savePermission(key);
  }

  /** True while a save for this section is in progress; drives the spinner on its card */
  isSaving(id: SaveId): boolean {
    return this.saveQueues[id].inFlight;
  }

  /** Saves a permission's toggle and selected users together; called on toggle and user changes */
  savePermission(key: ApplicationPermissionKey): void {
    const label = PERMISSIONS.find((permission) => permission.key === key)?.label;
    this.enqueueSave(
      key,
      () => {
        const snapshot = { ids: [...this.selected[key]], enable: this.enabled[key] };
        return {
          request$: this._AdminSettingsService.updateApplicationPermission(key, {
            allowedEmployeeIds: snapshot.ids,
            enable: snapshot.enable,
          }),
          commit: () => (this.saved.permissions[key] = snapshot),
        };
      },
      () => this.restorePermission(key),
      `Failed to save ${label} permission`
    );
  }

  /** Saves both stale data warning toggles; called when either toggle changes */
  saveStaleDataNotification(): void {
    this.enqueueSave(
      'staleData',
      () => {
        const snapshot = { ...this.staleDataNotification };
        return {
          request$: this._AdminSettingsService.updateStaleDataNotification(snapshot),
          commit: () => (this.saved.staleDataNotification = snapshot),
        };
      },
      () => (this.staleDataNotification = { ...this.saved.staleDataNotification }),
      'Failed to save stale data warning'
    );
  }

  /** Saves the co-applicant History / Remove / Restore toggles; called when any of them changes */
  saveCoApplicantRemoveRestore(): void {
    this.enqueueSave(
      'coApplicant',
      () => {
        const snapshot = { ...this.coApplicantRemoveRestore };
        return {
          request$: this._AdminSettingsService.updateCoApplicantRemoveRestore(snapshot),
          commit: () => (this.saved.coApplicantRemoveRestore = snapshot),
        };
      },
      () => (this.coApplicantRemoveRestore = { ...this.saved.coApplicantRemoveRestore }),
      'Failed to save co-applicant remove/restore'
    );
  }

  /**
   * Saves one section at a time so responses can't land out of order; changes made meanwhile go out
   * together in one follow-up save. On failure the section returns to the last saved state.
   */
  private enqueueSave(
    id: SaveId,
    buildSave: () => { request$: Observable<SaveResponse>; commit: () => void },
    rollback: () => void,
    errorMessage: string
  ): void {
    const queue = this.saveQueues[id];
    if (queue.inFlight) {
      queue.pending = true;
      return;
    }
    queue.inFlight = true;
    const { request$, commit } = buildSave();
    request$.subscribe({
      next: (res) => {
        commit();
        queue.inFlight = false;
        if (queue.pending) {
          queue.pending = false;
          this.enqueueSave(id, buildSave, rollback, errorMessage);
        } else {
          this.showMessage(res.message);
        }
      },
      error: (error) => {
        queue.inFlight = false;
        queue.pending = false;
        rollback();
        this.showMessage(error.error?.message || errorMessage);
      },
    });
  }

  /** Puts a permission's toggle and users back to the last saved state */
  private restorePermission(key: ApplicationPermissionKey): void {
    this.selected[key] = [...this.saved.permissions[key].ids];
    this.enabled[key] = this.saved.permissions[key].enable;
  }

  /** Shows a short dismissible snackbar */
  private showMessage(message: string): void {
    this._SnackBar.open(message, SnackbarConstants.ACTION_DISMISS, SnackbarConstants.DURATION_DISMISS_SHORT);
  }
}
